// @ts-nocheck
import { addYears, subYears } from 'date-fns'
import orderBy from 'lodash/orderBy'
import { action, computed, extendObservable, makeObservable, observable } from 'mobx'
import { dependsOn } from 'mobx-async'
import { computedRouteParam } from '@stores/computedRouteParam'
import slugify from 'slugify'

import {
  BookingPeriodReference,
  ServiceRequestStatusFilter,
  TemporalCondition,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate, OptionProps } from '@avantstay/backoffice-core'

import { ValueAddedServicesRequestsOtherFilteringProps } from '@domains/bookings/ValueAddedServicesRequests/ValueAddedServicesRequestsFilters/_types'

import { PeriodFilterRawData } from '@new/__global/components/Filters/Filters.types'
import { AllRegions } from '@new/__global/constants/filter'
import { BookingValueAddedServicesStore } from '@new/domains/bookings/stores/BookingValueAddedServicesStore'

import bookingServices from '@arriere/modules/bookingServices'
import { ROUTES } from '@routes'
import { RegionsStore } from '@stores/RegionsStore'

import {
  BookingDatesRawData,
  BookingsWithServicesRequests,
  BookingsWithServicesRequestsCount,
  ServicesRequestsCount,
} from '../screens/bookingsList/BookingsList.types'
import {
  formatRawPeriodValue,
  parsePeriodFilters,
  parseRawPeriodValue,
} from '../screens/bookingsList/BookingsList.utils'

const FIVE_YEARS = 5
const TWO_YEARS = 2
const DEFAULT_TO_DATE = formatDate(addYears(new Date(), FIVE_YEARS))
const DEFAULT_FROM_DATE = formatDate(subYears(new Date(), TWO_YEARS))

export const emptyOtherFilters = {
  serviceIds: [],
  checkIn: undefined,
  checkOut: undefined,
  creationDate: undefined,
}

class _BookingsServicesStore {
  serviceRequestPagination = { page: 1, size: 30 }

  filterTerm = ''

  activeFiltersLength = 0

  filtersCache: ValueAddedServicesRequestsOtherFilteringProps = emptyOtherFilters

  bookingsServicesRequestsCache?: BookingsWithServicesRequests = undefined

  bookingsServicesRequestsCountCache?: ServicesRequestsCount[] = undefined

  serviceRequestStatus = computedRouteParam<ServiceRequestStatusFilter>('status', {
    patterns: [ROUTES.bookingServicesRequests.path],
    parse: v => ServiceRequestStatusFilter[v]?.toUpperCase(),
    format: v => v?.toLowerCase(),
    defaultValue: ServiceRequestStatusFilter.all,
  })

  service = computedRouteParam<string[] | undefined>('service', {
    patterns: [ROUTES.bookingServicesRequests.path],
    defaultValue: undefined,
  })

  region = computedRouteParam<string | undefined>('market', {
    patterns: [ROUTES.bookingServicesRequests.path],
    format: (v?: string) => (v ? slugify(v, { lower: true }) : ''),
    defaultValue: AllRegions.value,
  })

  periodReference = computedRouteParam<BookingPeriodReference | undefined>('periodReference', {
    patterns: [ROUTES.bookingServicesRequests.path],
  })

  dateRangeFilterParam = computedRouteParam<string>('date', {
    patterns: [ROUTES.bookingServicesRequests.path],
    defaultValue: `${DEFAULT_FROM_DATE}_${DEFAULT_TO_DATE}`,
  })

  checkIn = computedRouteParam<BookingDatesRawData | undefined>('checkIn', {
    patterns: [ROUTES.bookingServicesRequests.path],
    format: formatRawPeriodValue,
    parse: valueString => (valueString ? parseRawPeriodValue(valueString) : undefined),
    defaultValue: { periodReference: TemporalCondition.equals },
  })

  checkOut = computedRouteParam<BookingDatesRawData | undefined>('checkOut', {
    patterns: [ROUTES.bookingServicesRequests.path],
    format: formatRawPeriodValue,
    parse: valueString => (valueString ? parseRawPeriodValue(valueString) : undefined),
    defaultValue: { periodReference: TemporalCondition.equals },
  })

  creationDate = computedRouteParam<BookingDatesRawData | undefined>('creationDate', {
    patterns: [ROUTES.bookingServicesRequests.path],
    format: formatRawPeriodValue,
    parse: valueString => (valueString ? parseRawPeriodValue(valueString) : undefined),
    defaultValue: { periodReference: TemporalCondition.equals },
  })

  constructor() {
    makeObservable(this, {
      serviceRequestPagination: observable,
      filterTerm: observable,
      activeFiltersLength: observable,
      filtersCache: observable,
      bookingsServicesRequestsCache: observable,
      bookingsServicesRequestsCountCache: observable,
      periodReference: observable,
      activeRegion: computed,
      serviceOptions: computed,
      bookingsServicesRequests: computed,
      bookingsServicesRequestsCount: computed,
      advancedFiltersFromServer: computed,
      setBookingsServicesRequestsCache: action,
      setBookingsServicesRequestsCountCache: action,
      setFilterTerm: action.bound,
      setRegion: action.bound,
      setBookingPeriodReference: action.bound,
      setServiceRequestStatus: action.bound,
      setServiceRequest: action.bound,
      setServiceRequestPage: action.bound,
      setServiceRequestPageSize: action.bound,
      clearOtherFilters: action.bound,
      clearServiceRequestFilters: action.bound,
      setActiveFiltersLength: action.bound,
      setFiltersCache: action.bound,
      setDateRangeFilter: action.bound,
      setCheckIn: action.bound,
      setCheckOut: action.bound,
      setCreationDate: action.bound,
    })
  }

  get activeRegion() {
    const region = this.region.get()

    return (async (): Promise<OptionProps | undefined> => {
      const options = await RegionsStore.regionsOptions

      if (region === AllRegions.value) {
        return AllRegions as OptionProps
      }

      return options.find((it: OptionProps) => it.value === region)
    })()
  }

  get serviceOptions() {
    return bookingServices.getServices().then(services => {
      const options = services.map(service => ({
        value: service.id,
        label: service.name,
        hasFullWidth: true,
      }))

      const sortedOptions = orderBy(options, ['label'], ['asc'])

      return sortedOptions || []
    })
  }

  get bookingsServicesRequests(): Promise<BookingsWithServicesRequests> {
    const pagination = this.serviceRequestPagination
    const [from, to] = this.dateRangeFilterParam.get().split('_')
    const filter = this.filterTerm
    const status = this.serviceRequestStatus.get()
    const serviceIds = this.service.get()
    const checkIn = this.checkIn.get()
    const checkOut = this.checkOut.get()
    const creationDate = this.creationDate.get()

    dependsOn(
      this.activeRegion,
      BookingValueAddedServicesStore.confirmServiceRequest,
      BookingValueAddedServicesStore.rejectServiceRequest,
    )

    const dates =
      checkIn || checkOut || creationDate
        ? {
            checkIn: parsePeriodFilters({
              period: checkIn?.period,
              periodReference: checkIn?.periodReference,
            }),
            checkOut: parsePeriodFilters({
              period: checkOut?.period,
              periodReference: checkOut?.periodReference,
            }),
            creationDate: parsePeriodFilters({
              period: creationDate?.period,
              periodReference: creationDate?.periodReference,
            }),
          }
        : undefined

    return this.activeRegion.then(activeRegion => {
      const regionId =
        activeRegion?.value === AllRegions.value ? undefined : (activeRegion?.value as string)

      return bookingServices.getBookingsWithServicesRequests(
        regionId,
        filter,
        from,
        to,
        serviceIds,
        status,
        pagination,
        dates,
      )
    })
  }

  get bookingsServicesRequestsCount(): Promise<
    {
      service: ServiceRequestStatusFilter
      count: BookingsWithServicesRequestsCount
    }[]
  > {
    const pagination = {
      page: 1,
      size: 1,
    }
    const [from, to] = this.dateRangeFilterParam.get().split('_')
    const filter = this.filterTerm
    const serviceIds = this.service.get()
    const checkIn = this.checkIn.get()
    const checkOut = this.checkOut.get()
    const creationDate = this.creationDate.get()

    dependsOn(
      this.activeRegion,
      BookingValueAddedServicesStore.confirmServiceRequest,
      BookingValueAddedServicesStore.rejectServiceRequest,
    )

    const dates =
      checkIn || checkOut || creationDate
        ? {
            checkIn: parsePeriodFilters({
              period: checkIn?.period,
              periodReference: checkIn?.periodReference,
            }),
            checkOut: parsePeriodFilters({
              period: checkOut?.period,
              periodReference: checkOut?.periodReference,
            }),
            creationDate: parsePeriodFilters({
              period: creationDate?.period,
              periodReference: creationDate?.periodReference,
            }),
          }
        : undefined

    return this.activeRegion.then(activeRegion => {
      const regionId =
        activeRegion?.value === AllRegions.value ? undefined : (activeRegion?.value as string)

      const promises = Object.values(ServiceRequestStatusFilter).map(statusItem =>
        bookingServices
          .getBookingsWithServicesRequestsCount(
            regionId,
            filter,
            from,
            to,
            serviceIds,
            statusItem,
            pagination,
            dates,
          )
          .then(response => ({
            service: statusItem,
            count: response,
          })),
      )
      return Promise.all(promises)
    })
  }

  get advancedFiltersFromServer(): Promise<
    ValueAddedServicesRequestsOtherFilteringProps | undefined
  > {
    const serviceIds = this.service.get()
    const checkIn = this.checkIn.get()
    const checkOut = this.checkOut.get()
    const creationDate = this.creationDate.get()

    const filters: ValueAddedServicesRequestsOtherFilteringProps = {
      serviceIds,
      checkIn: checkIn?.period ? checkIn : undefined,
      checkOut: checkOut?.period ? checkOut : undefined,
      creationDate: creationDate?.period ? creationDate : undefined,
    }

    return Promise.resolve().then(() => {
      this.setFiltersCache(filters)
      return filters
    })
  }

  setBookingsServicesRequestsCache(bookingsServicesRequestsCache?: BookingsWithServicesRequests) {
    this.bookingsServicesRequestsCache = bookingsServicesRequestsCache
  }

  setBookingsServicesRequestsCountCache(
    bookingsServicesRequestsCountCache?: ServicesRequestsCount[],
  ) {
    this.bookingsServicesRequestsCountCache = bookingsServicesRequestsCountCache
  }

  setFilterTerm(term: string) {
    this.filterTerm = term
  }

  setRegion(selectedOption?: OptionProps<string>) {
    this.setServiceRequestPage(0)
    this.region.push(selectedOption?.value as string)
  }

  setBookingPeriodReference(parsedFilters?: BookingPeriodReference) {
    this.setServiceRequestPage(0)
    this.periodReference.replace(parsedFilters)
  }

  setServiceRequestStatus(selectedOption: OptionProps) {
    this.setServiceRequestPage(0)
    this.serviceRequestStatus.push(selectedOption?.label as ServiceRequestStatusFilter)
  }

  setServiceRequest(serviceIds?: string[]) {
    this.setServiceRequestPage(0)
    this.service.push(serviceIds)
  }

  setServiceRequestPage(page: number) {
    this.serviceRequestPagination = {
      ...this.serviceRequestPagination,
      page: page + 1,
    }
  }

  setServiceRequestPageSize(size: number) {
    this.serviceRequestPagination = {
      size,
      page: 1,
    }
  }

  clearOtherFilters() {
    this.setCheckIn()
    this.setCheckOut({})
    this.setCreationDate({})
    this.setServiceRequest()
  }

  clearServiceRequestFilters() {
    this.setServiceRequestPage(0)
    this.setRegion()
    this.setServiceRequest()
    this.serviceRequestStatus.push(ServiceRequestStatusFilter.all)
    this.setFilterTerm('')
  }

  setActiveFiltersLength(value: number) {
    this.activeFiltersLength = value
  }

  setFiltersCache = (filters: ValueAddedServicesRequestsOtherFilteringProps): void => {
    extendObservable(this.filtersCache, filters)
  }

  setDateRangeFilter = (date?: PeriodFilterRawData): void => {
    this.dateRangeFilterParam.replace(date?.period || undefined)
  }

  setCheckIn(value?: BookingDatesRawData) {
    this.checkIn.replace({
      period: value?.period,
      periodReference: value?.periodReference,
    })
  }

  setCheckOut(value: BookingDatesRawData) {
    const { period, periodReference } = value

    this.checkOut.replace({ period, periodReference })
  }

  setCreationDate(value: BookingDatesRawData) {
    const { period, periodReference } = value

    this.creationDate.replace({ period, periodReference })
  }
}

export const BookingsServicesStore = new _BookingsServicesStore()
